// Generated by tutorials/record.cjs from tutorials/articles/h76-menu-and-nourishment-cost.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h76 --text-only` (wording) or
// `node tutorials/record.cjs h76` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'menu-and-nourishment-cost',
  catalogId: 'h76',
  title: 'Menu and nourishment cost',
  summary: 'See what your menu costs per resident, day by day and meal by meal, and what your nourishments cost by unit, worked out from the menu cycle, the residents\' orders and each item\'s cost.',
  audience: 'Menu planners, dietary managers and food service directors who watch food cost',
  area: 'reports',
  learn: [
    'Cost a week of the menu for a typical resident',
    'Read the cost of each day and meal, and the averages',
    'Find the items that have no cost yet',
    'Cost the nourishments, unit by unit',
    'Know what changes the numbers',
  ],
  keywords: [
    'cost',
    'menu cost',
    'food cost',
    'meal cost',
    'cost per day',
    'cost per meal',
    'raw food cost',
    'cost analysis',
    'nourishment cost',
    'snack cost',
    'supplement cost',
    'item cost',
    'no cost',
    'zero cost',
    'typical resident',
    'budget',
    'average cost',
  ],
  steps: [
    {
      id: 'open',
      part: 'Cost a week of the menu',
      heading: 'Open Menu Cost',
      text: 'In the menu, select Reports, then Menu Cost under Cost & Planning.\n\nMenu Cost works out what one resident\'s meals cost: the items their tray ticket gets from the menu cycle on each day, priced at each item\'s cost.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/open.webp',
          width: 860,
          height: 1222,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/open.mp4',
          width: 860,
          height: 1222,
          durationSec: 6,
        },
        alt: 'The Menu Cost page: Typical Resident, the dates, the menu cycle in use, Options and Mealtimes.',
      },
    },
    {
      id: 'resident',
      part: 'Cost a week of the menu',
      heading: 'Pick a typical resident',
      text: 'Pick the Typical Resident: someone whose diet order is like most of your residents\'. The report costs the menu as their tray ticket has it, so a resident on a therapeutic diet or a texture change gets that diet\'s items and portions.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/resident.webp',
          width: 860,
          height: 1222,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/resident.mp4',
          width: 860,
          height: 1222,
          durationSec: 5.1,
        },
        alt: 'Typical Resident set to Whitfield, Eleanor.',
      },
    },
    {
      id: 'dates',
      part: 'Cost a week of the menu',
      heading: 'Choose the dates and meals',
      text: 'Set the first date and, in To, the last. Under them, Menu Cycle shows the cycle and day in use on the first date.\n\nCheck the Mealtimes to cost: Breakfast, Lunch and Dinner for the meals, and any nourishment times as well.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/dates.webp',
          width: 860,
          height: 1222,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/dates.mp4',
          width: 860,
          height: 1222,
          durationSec: 6,
        },
        alt: 'Dates from today to six days from now, the menu cycle in use, and Breakfast, Lunch and Dinner checked.',
      },
    },
    {
      id: 'options',
      part: 'Cost a week of the menu',
      heading: 'Pick the options',
      text: 'Show Menu Categories breaks each meal\'s cost down by menu category, such as Entree, Vegetable and Dessert.\n\nShow Item List with no Cost, checked to start, adds a list of the items that were served but have a cost of 0.00.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/options.webp',
          width: 860,
          height: 1222,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/options.mp4',
          width: 860,
          height: 1222,
          durationSec: 2.9,
        },
        alt: 'Options: Show Menu Categories checked, and Show Item List with no Cost checked.',
      },
    },
    {
      id: 'view',
      part: 'Cost a week of the menu',
      heading: 'Generate and open it',
      text: 'Select Generate. The report is made in the Print Queue: select Open Print Queue in the message. When the report says Completed, select it and select Print to open it.\n\nEach day has a heading with its menu day, date and cost. Under it is each meal\'s cost, and with Show Menu Categories, each category\'s share.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/view.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/view.mp4',
          width: 1280,
          height: 800,
          durationSec: 6,
        },
        alt: 'The Menu Cost report: Menu Cost for a Regular diet, Day headings with the date and the day\'s cost, then Breakfast, Lunch and Dinner with their categories.',
      },
    },
    {
      id: 'summary',
      part: 'Read the averages and fix missing costs',
      heading: 'Read the averages',
      text: 'After the last day, Summary gives the average cost of each meal over the dates, with each category\'s average under it, and Days: the average cost of a whole day.\n\nDays is the number to compare with your budget per resident day.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/summary.webp',
          width: 1280,
          height: 798,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/summary.mp4',
          width: 1280,
          height: 798,
          durationSec: 5.8,
        },
        alt: 'The Summary: the average cost of Breakfast, Lunch and Dinner with their categories, and the Days average.',
      },
    },
    {
      id: 'no-cost',
      part: 'Read the averages and fix missing costs',
      heading: 'Find items with no cost',
      text: 'The last page, Items With No Cost, lists every item that was served in the dates with a cost of 0.00, and how many times.\n\nThose items add nothing to the totals, so the totals look lower than they are. Give each one its Cost on the item (Food Data, Items), then run the report again.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/no-cost.webp',
          width: 1280,
          height: 798,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/no-cost.mp4',
          width: 1280,
          height: 798,
          durationSec: 3.4,
        },
        alt: 'Items With No Cost: Cranberry Relish, Garden Herb Rice and Sugar Free Gelatin, each with a count.',
      },
    },
    {
      id: 'what-changes',
      part: 'Read the averages and fix missing costs',
      heading: 'What changes the numbers',
      text: 'The costs follow what is in Dietech:\n\n- **Item costs.** Each item\'s Cost is for its default size. Another size is costed in proportion, and a half or double portion at half or double.\n- **The menu cycle.** The items on each day, and which cycle is in use on the dates.\n- **The typical resident.** Their diet order, consistency and portions decide which items their tray ticket gets.\n- **The meals you check.** Leave a meal out and its cost is left out of the day.',
      textOnly: true,
    },
    {
      id: 'nourish-open',
      part: 'Cost the nourishments',
      heading: 'Open Nourishment Cost',
      text: 'Close the report, and select Nourishment Cost in the menu.\n\nNourishment Cost counts the nourishments your residents get at the snack times you choose, from their orders, and prices them at each item\'s cost.\n\nPick By Unit for a page per unit, or Total for one list for the facility.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/nourish-open.webp',
          width: 860,
          height: 1222,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/nourish-open.mp4',
          width: 860,
          height: 1222,
          durationSec: 3.5,
        },
        alt: 'The Nourishment Cost page: Report Type By Unit or Total, the dates, the menu cycle, and the Mealtimes, Floor/Units and Residents tabs.',
      },
    },
    {
      id: 'nourish-pick',
      part: 'Cost the nourishments',
      heading: 'Pick the snack times',
      text: 'Set the dates. On the Mealtimes tab, check the snack times to cost. Floor/Units and Residents start with everyone checked; clear any you want left out.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/nourish-pick.webp',
          width: 860,
          height: 1222,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/nourish-pick.mp4',
          width: 860,
          height: 1222,
          durationSec: 6,
        },
        alt: 'The Floor/Units tab with Maple, Birch and Willow checked, after AM, PM and HS were checked on the Mealtimes tab.',
      },
    },
    {
      id: 'nourish-view',
      part: 'Cost the nourishments',
      heading: 'Read the nourishment cost',
      text: 'Select Generate, then open the report from the Print Queue, as for Menu Cost.\n\nEach unit lists its nourishments with how many are served at each snack time, the Item Cost of one, and the Total Cost. The unit\'s total is under its list, and the report total at the end.',
      media: {
        image: {
          src: '/help/menu-and-nourishment-cost/nourish-view.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/menu-and-nourishment-cost/nourish-view.mp4',
          width: 1280,
          height: 800,
          durationSec: 6,
        },
        alt: 'The Nourishment Summary by Unit: the Birch unit\'s nourishments with counts at each snack time, Item Cost, Total Cost and the unit\'s total.',
      },
    },
  ],
  tips: [
    'Run Menu Cost for a whole week, or a whole cycle, so the Summary gives you a fair average per day.',
    'Run it once for a resident on a regular diet and once for a common therapeutic diet to see what the diet changes.',
    'Keep Show Item List with no Cost checked. Every item on that list makes the totals look lower than they are.',
  ],
  notes: [
    'The costs are the item costs in Dietech, not invoices. They are only as current as the Cost on each item.',
    'Both reports are also under Reports, as Cost Analysis - Menu and Cost Analysis - Nourishments, and both are kept in the Print Queue like any report.',
    'Menu Cost and Nourishment Cost need the Analysis area on your role. Ask your administrator.',
  ],
  related: ['running-a-report', 'items-and-recipes', 'menu-cycles', 'meal-patterns'],
  recorded: '2026-11-03',
};

export default article;
